import { useState, useEffect, Fragment, useMemo, useCallback } from 'react';
import { Card, Snackbar, Snack } from '@nike/eds';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams, useRevalidator } from 'react-router-dom';
import newProductWallClient from '../../clientAPI/newproductwall';
import { useDebouncedCallback } from 'use-debounce';
import * as R from 'ramda';
import { fetchConceptsByIds } from '../../utils/conceptHelpers';

import { useInView } from 'react-intersection-observer';
import Loader from 'components/Loader';
import ProductWallItem from 'components/NewProductWall/ProductWallItem';
import {
  productWallErrorMessageAtom,
  productWallHasErrorAtom,
} from '../../atoms/productwall';
import { useAtom } from 'jotai';
import { Explain } from 'components/Explain/newproductwall/Explain';
import { Filter, FilterBreadcrumbs } from 'components/NewProductWall/Filter';
import parseArrayFromUrlString from '../../utils/parseArrayFromUrlString';
import propagate403Error from '../../utils/propagate403Error';
import Dropdown from 'components/Dropdown/Dropdown';
import { PWALL_SORT_OPT_PARAMS } from 'constants/index';
import { Product, ProductWallGroupingResponse } from 'types/newproductwall';

const sortOptions = [
  { label: '精选推荐', value: 'featured' },
  { label: '最新', value: 'newest' },
  { label: '价格：由高到低', value: 'priceDesc' },
  { label: '价格：由低到高', value: 'priceAsc' },
];

export default function ProductWallResults({
  searchTerms,
  consumerChannelId,
  language,
  marketplace,
  callerId,
  userAgent,
  searchOptions,
  showExplainPanel,
  taxonomyIds,
  setTaxonomyIds,
  selectedConcepts,
  setSelectedConcepts,
}: {
  searchTerms: string;
  consumerChannelId: string;
  language: string;
  marketplace: string;
  callerId: string;
  userAgent: string;
  searchOptions: keyable;
  showExplainPanel: string;
  taxonomyIds: Array<string>;
  setTaxonomyIds: (taxonomyIds: Array<string>) => void;
  selectedConcepts: RecommendConcept[];
  setSelectedConcepts: (concepts: RecommendConcept[]) => void;
}) {
  const revalidator = useRevalidator();
  const [searchParams, setSearchParams] = useSearchParams();

  const taxonomyIdsUpdater = (val: Array<string>) => {
    setTaxonomyIds(val);
    if (val.length === 0) {
      searchParams.set('taxonomyIds', '');
      setSelectedConcepts([]);
    } else {
      const urlVal = encodeURIComponent(JSON.stringify(val));
      searchParams.set('taxonomyIds', urlVal);

      // Fetch full concept objects to sync selectedConcepts
      fetchConceptsByIds(val, (id, err) => {
        setErrorMsg(`Failed to load concept: ${id}`);
        setHasError(true);
      }).then((validConcepts) => {
        setSelectedConcepts(validConcepts);
      });
    }
    setSearchParams(searchParams);
  };

  const [nextMsg, setNextMsg] = useState('');

  const [errorMsg, setErrorMsg] = useAtom(productWallErrorMessageAtom);
  const [hasError, setHasError] = useAtom(productWallHasErrorAtom);

  const [sortBy, setSortBy] = useState(sortOptions[0].value);
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);
  const handleSortSelection = (opt: { value: string }) => {
    const i = sortOptions.findIndex((o) => o.value === opt.value);
    setSelectedOptionIdx(i);
    setSortBy(opt.value);
  };

  // Nike.com channel ID
  const NIKE_COM_CHANNEL_ID = 'd9a5bc42-4b9c-4976-858a-f159cf99c647';
  const isNikeComChannel = consumerChannelId === NIKE_COM_CHANNEL_ID;

  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: [
      'newProductWall',
      searchTerms,
      consumerChannelId,
      language,
      marketplace,
      callerId,
      userAgent,
      searchOptions,
      sortBy,
      // For Nike.com: use navigationIds from selectedConcepts
      // For other channels: use taxonomyIds
      isNikeComChannel
        ? selectedConcepts.map((c) => c.navigationId).join(',')
        : taxonomyIds.join(','),
    ],
    getNextPageParam: (lastPage: ProductWallGroupingResponse) => {
      const nextPage = lastPage.pages?.next;
      return nextPage ? nextPage : undefined;
    },
    queryFn: async ({ pageParam = '' }) => {
      // For Nike.com channel: build path parameter by joining navigationIds with 'z'
      // For other channels: use attributeIds array
      let path: string | undefined = undefined;

      if (isNikeComChannel) {
        // Extract navigationId from each concept, filter out undefined/null values
        const navigationIds = selectedConcepts
          .map((concept) => concept.navigationId)
          .filter((navId) => navId != null && navId !== '');

        if (navigationIds.length > 0) {
          path = `/w/${navigationIds.join('z')}`;
        } else {
          path = `/w/`;
        }
      }

      return newProductWallClient.loadNextPage({
        anchor: 0,
        count: 24,
        searchTerms: searchTerms,
        language: language,
        marketplace: marketplace,
        channelId: consumerChannelId,
        attributeIds: isNikeComChannel ? undefined : taxonomyIds,
        path: path,
        sort: sortBy,
        queryType: pageParam ? 'PRODUCTS' : 'ALL',
        nextUrl: pageParam,
        options: searchOptions,
        headerCallerId: callerId,
        headerUserAgent: userAgent,
      });
    },
    onSuccess: (_data) => {
      setNextMsg('No more data');
    },
    onError: (err: keyable) => {
      setErrorMsg(err.message || JSON.stringify(err));
      setHasError(true);
      setNextMsg('Error');
      err.status === 401 && revalidator.revalidate();
    },
    enabled: !!searchTerms,
  });

  propagate403Error(error as keyable);

  const { ref, inView } = useInView();

  const debouncedFetchNextPage = useDebouncedCallback(
    () => fetchNextPage(),
    300,
  );

  // Reset messages when search parameters change
  useEffect(() => {
    setNextMsg('');
  }, [
    searchTerms,
    consumerChannelId,
    language,
    marketplace,
    searchOptions,
    sortBy,
    taxonomyIds,
    selectedConcepts,
  ]);

  useEffect(() => {
    if (inView) {
      debouncedFetchNextPage();
    }
  }, [inView, debouncedFetchNextPage]);

  return (
    <div>
      <div className='tw-relative'>
        <div className='tw-flex tw-justify-between'>
          <p className='eds-type--display-5 tw-mb-5'>
            Total:{' '}
            {isFetching
              ? 'Loading'
              : data?.pages?.[0].pages?.totalResources || 0}
          </p>
          <div>
            <Dropdown
              anchor={'right'}
              defaultText={'排序依据'}
              onDropDownSelect={handleSortSelection}
              options={sortOptions}
              selectedItemPrefex={`排序依据: `}
              selectedOption={selectedOptionIdx}
            />
          </div>
        </div>
        <div className='tw-mb-1'>
          <FilterBreadcrumbs
            channelId={consumerChannelId}
            language={language}
            marketplace={marketplace}
            taxonomyIds={taxonomyIds}
            searchTerms={searchTerms}
            searchOptions={searchOptions}
            onChange={taxonomyIdsUpdater}
            callerId={callerId}
            userAgent={userAgent}
            data={data?.pages?.[0]?.navigation}
          />
        </div>
        <div className='tw-flex tw-relative tw-gap-1'>
          <div
            style={{ flex: '0 0 240px' }}
            className='tw-pb-4 tw-pr-4 tw-max-h-screen tw-overflow-y-scroll tw-sticky tw-top-0'
          >
            <Filter
              channelId={consumerChannelId}
              language={language}
              marketplace={marketplace}
              taxonomyIds={taxonomyIds}
              searchTerms={searchTerms}
              searchOptions={searchOptions}
              onChange={taxonomyIdsUpdater}
              callerId={callerId}
              userAgent={userAgent}
              data={data?.pages?.[0]?.navigation}
            />
          </div>
          <div className='tw-flex tw-flex-wrap tw-gap-5 tw-content-start'>
            {data?.pages.map((page) => {
              return (
                <Fragment key={page.pages?.next}>
                  {page.productGroupings?.map((p, index) => (
                    <ProductWallItem
                      key={`${page.pages?.next}-${index}`}
                      data={p}
                    />
                  ))}
                </Fragment>
              );
            })}
            {(isFetching || isFetchingNextPage) && (
              <div className='tw-w-full tw-flex tw-justify-center tw-items-center'>
                <Loader />
              </div>
            )}
            <div className='tw-h-2' ref={ref} />
            {!hasNextPage && !isFetchingNextPage && !isFetching && (
              <div className='tw-w-full tw-flex tw-justify-center tw-items-center'>
                <p className='tw-text-gray-500'>{nextMsg}</p>
              </div>
            )}
          </div>
        </div>
        {showExplainPanel === 'true' && (
          <div className='tw-absolute tw-right-0 tw-top-0 tw-max-w-full tw-overflow-auto tw-bg-gray-100 eds-elevation--4'>
            <Card className='tw-px-12'>
              <Explain
                searchTerms={searchTerms}
                marketplace={marketplace}
                language={language}
                consumerChannelId={consumerChannelId}
                searchOptions={searchOptions}
                sort={sortBy}
              />
            </Card>
          </div>
        )}
      </div>
      <Snackbar style={{ left: '90%', top: '90%' }}>
        {errorMsg && (
          <Snack
            autoDismissDuration={5000}
            id='pw-error-notification'
            onDismiss={(_id) => {
              setErrorMsg('');
            }}
            status={hasError ? 'error' : 'success'}
          >
            {errorMsg}
          </Snack>
        )}
      </Snackbar>
    </div>
  );
}
